import { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  debounce,
  fetchData,
  formatDate,
  formatNumber,
  toasterrormsg,
  useMenuIdPermissions,
} from '../../../reusable'
import { KTIcon, toAbsoluteUrl } from '../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import DataTable from 'react-data-table-component'
import Loader from '../components/Loader'
import { Tr } from '../../../_metronic/i18n/Tr'
import { Modal } from 'react-bootstrap'


const SearchAdImpression: FC = () => {
  const t = Tr()
  const { menuId } = useMenuIdPermissions()

  // Impression report state
  const [reportList, setReportList] = useState<any[]>([])
  const [inputValue, setInputValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const [sortField, setSortField] = useState('')
  const [sortOrder, setSortOrder] = useState('')
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [focus, setFocus] = useState('')

  const [showUsersModal, setShowUsersModal] = useState(false)
  const [selectedSponser, setSelectedSponser] = useState<any>(null)
  const [usersList, setUsersList] = useState<any[]>([])
  const [imageBaseUrl, setImageBaseUrl] = useState('')
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersTotal, setUsersTotal] = useState(0)
  const [usersLimit, setUsersLimit] = useState(10)
  const [usersOffset, setUsersOffset] = useState(0)
  const [usersTerm, setUsersTerm] = useState('')

  // const [selectedUser, setSelectedUser] = useState<any>(null)
  // const [userHistory, setUserHistory] = useState<any[]>([])
  // const [historyLoading, setHistoryLoading] = useState(false)
  // const [historyLoadingMore, setHistoryLoadingMore] = useState(false)
  // const [historyTotal, setHistoryTotal] = useState(0)
  // const [historyOffset, setHistoryOffset] = useState(0)
  // const [historyHasMore, setHistoryHasMore] = useState(false)

  // const historyScrollRef = useRef<HTMLDivElement | null>(null)

  const fetchImpressionReport = async () => {
    try {
      if (!isDataLoaded) setIsLoading(true)
      const param = {
        limit,
        offset,
        term: searchTerm.trim(),
        sortBy: sortField,
        sort: sortOrder,
      }
      const response = await fetchData('admin/sponser/impression/report', param)
      if (response.status == '200') {
        const data = response?.data ?? {}
        setTotalRecords(data.totalRecord || 0)
        setReportList(data.list || [])
        setIsDataLoaded(true)
      } else {
        toasterrormsg(response?.message || 'An error occurred')
        setReportList([])
        setTotalRecords(0)
      }
    } catch (error) {
      console.error('Error fetching impression report:', error)
      toasterrormsg('Failed to fetch impression report')
      setReportList([])
      setTotalRecords(0)
    } finally {
      setIsDataLoaded(true)
      setIsLoading(false)
    }
  }

  // ── View Users API ──
  const fetchViewUsers = async (sponserId: number, lmt = usersLimit, off = usersOffset, term = usersTerm) => {
    try {
      setUsersLoading(true)
      const param = { sponserId, limit: lmt, offset: off, term: term.trim() }
      const response = await fetchData('admin/sponser/view/list', param)
      if (response.status == '200') {
        const data = response?.data ?? {}
        setUsersTotal(data.totalRecord || 0)
        setUsersList(data.list || [])
        if (response.url) setImageBaseUrl(response.url)
      } else {
        toasterrormsg(response?.message || 'An error occurred')
        setUsersList([])
        setUsersTotal(0)
      }
    } catch (error) {
      console.error('Error fetching view users:', error)
      toasterrormsg('Failed to fetch sponser view users')
      setUsersList([])
      setUsersTotal(0)
    } finally {
      setUsersLoading(false)
    }
  }

  // ── Handlers ──
  const openUsersModal = (row: any) => {
    setSelectedSponser(row)
    setUsersOffset(0)
    setUsersTerm('')
    setShowUsersModal(true)
    fetchViewUsers(row.sponserId, usersLimit, 0, '')
  }

  const closeUsersModal = () => {
    setShowUsersModal(false)
    setSelectedSponser(null)
    setUsersList([])
  }

  // ── Search ──
  const sponserSearch = (text: string, isfoc: any) => {
    setInputValue(text)
    setFocus(isfoc)
    debouncedSearch(text)
  }

  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearchTerm(value)
      setOffset(0)
    }, 500)
  ).current

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [])

  // ── Sort ──
  const handleSort = (column: any, sortDirection: string) => {
    try {
      const fieldName = column.selector ? String(column.selector).split('.').pop() : ''
      setSortField(fieldName || '')
      setSortOrder(sortDirection === 'asc' ? 'asc' : 'desc')
    } catch (error) {
      console.error('Error in sorting:', error)
    }
  }

  // ── Pagination ──
  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / limit)
    if (newPage === 1) setOffset(0)
    else if (newPage === totalPages) setOffset((totalPages - 1) * limit)
    else setOffset((newPage - 1) * limit)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage)
    setOffset(0)
  }

  const handleUsersPageChange = (newPage: number) => {
    if (!selectedSponser) return
    const totalPages = Math.ceil(usersTotal / usersLimit)
    let newOffset = 0
    if (newPage === 1) newOffset = 0
    else if (newPage === totalPages) newOffset = (totalPages - 1) * usersLimit
    else newOffset = (newPage - 1) * usersLimit
    setUsersOffset(newOffset)
    fetchViewUsers(selectedSponser.sponserId, usersLimit, newOffset, usersTerm)
  }

  const handleUsersRowsPerPageChange = (newRowsPerPage: number) => {
    if (!selectedSponser) return
    setUsersLimit(newRowsPerPage)
    setUsersOffset(0)
    fetchViewUsers(selectedSponser.sponserId, newRowsPerPage, 0, usersTerm)
  }

  // ── Table Search Header ──
  const CustomHeader = (props: any) => {
    const inputRef: any = useRef(null)
    const handleChange = async (e: any) => {
      await props.customSearch(e, 1)
    }
    useEffect(() => {
      if (inputRef.current && props.isfoc === 1) inputRef.current.focus()
    }, [props.isfoc])

    return (
      <div className='d-flex justify-content-between w-100 my-5'>
        <div className='d-flex align-items-center position-relative my-1'>
          <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
          <input
            type='text'
            data-kt-user-table-filter='search'
            className='form-control form-control-solid w-250px ps-14'
            placeholder={t('Search')}
            value={props.search}
            onChange={(e: any) => handleChange(e.target.value)}
            ref={inputRef}
          />
        </div>
      </div>
    )
  }

  // ── Columns ──
  const columns = useMemo(
    () => [

      {
        name: t('Property Name'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.en_parentName ? row.en_parentName : (row.en_name || '-')}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.en_parentName || row.en_name,
        width: '180px',
      },
      {
        name: t('Listing Name'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.en_parentName ? (row.en_name || '-') : '-'}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.en_parentName ? row.en_name : '',
        width: '180px',
      },
      {
        name: t('Vendor'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.fullName || '-'}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.fullName,
        width: '160px',
      },
      {
        name: t('Impressions'),
        cell: (row: any) => (
          <div className='my-3'>
            <span className='imp-badge imp-badge--impressions'>
              <KTIcon iconName='eye' className='fs-4' />
              {(Number(row.totalImpressions) || 0).toLocaleString()}
            </span>
          </div>
        ),
        sortable: true,
        selector: (row: any) => Number(row.totalImpressions) || 0,
        width: '140px',
      },
      {
        name: t('Clicks'),
        cell: (row: any) => (
          <div className='my-3'>
            <span className='imp-badge imp-badge--count'>
              <KTIcon iconName='people' className='fs-4' />
              {(Number(row.totalViews) || 0).toLocaleString()}
            </span>
          </div>
        ),
        sortable: true,
        selector: (row: any) => Number(row.totalViews) || 0,
        width: '120px',
      },
      {
        name: t('CTR'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-6'>{row.ctr ? `${row.ctr}%` : '-'}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => Number(row.ctr) || 0,
        width: '100px',
      },
      {
        name: t('Duration'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-6'>
              {row.duration != null ? `${row.duration} ${row.durationType || ''}` : '-'}
            </div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.duration,
        width: '120px',
      },
      {
        name: t('Start Date'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-6'>
              {row.startDate ? formatDate(row.startDate, 'DD-MM-YYYY') : <span className='text-muted'>-</span>}
            </div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.startDate,
        width: '130px',
      },
      {
        name: t('End Date'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-6'>
              {row.endDate ? formatDate(row.endDate, 'DD-MM-YYYY') : <span className='text-muted'>-</span>}
            </div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => row.endDate,
        width: '130px',
      },
      {
        name: t('Payment Amount'),
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.price ? formatNumber(row.price, false) : '-'}</div>
          </div>
        ),
        sortable: true,
        selector: (row: any) => Number(row.price),
        width: '160px',
      },
      {
        name: t('View Users'),
        cell: (row: any) => (
          <div className='my-3'>
            <button
              className='btn btn-light-info btn-sm d-flex align-items-center gap-1 px-3 py-2 rounded-pill'
              title='View Users'
              onClick={() => openUsersModal(row)}
            >
              <KTIcon iconName='people' className='fs-4' iconType='solid' />
              <span className='fs-7'>{t('View Users')}</span>
            </button>
          </div>
        ),
        sortable: false,
        width: '150px',
      },
    ],
    [t]
  )

  // ── Users modal columns ──
  const usersColumns = useMemo(
    () => [
      {
        name: t('User'),
        cell: (row: any) => (
          <div className='d-flex align-items-center gap-3 my-2'>
            {row.profile ? (
              <img
                src={imageBaseUrl + row.profile}
                alt={row.fullName || ''}
                className='imp-user-profile-img'
                onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = toAbsoluteUrl('/media/avatars/blank.png')
                }}
              />
            ) : (
              <div className='imp-user-avatar'>
                {(row.fullName || row.userName || 'U').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className='fw-bold fs-6'>{row.fullName || row.userName || '-'}</div>
              {row.email && <div className='text-muted fs-8'>{row.email}</div>}
            </div>
          </div>
        ),
        sortable: false,
        grow: 2,
      },
      {
        name: t('Impressions'),
        cell: (row: any) => (
          <div className='my-2'>
            <span className='imp-badge imp-badge--impressions'>
              <KTIcon iconName='eye' className='fs-5' />
              {row.impressionCount || row.totalImpressions || 0}
            </span>
          </div>
        ),
        sortable: false,
        width: '140px',
      },
      {
        name: t('Clicks'),
        cell: (row: any) => (
          <div className='my-2'>
            <span className='imp-badge imp-badge--count'>
              <KTIcon iconName='cursor' className='fs-5' />
              {row.clickCount || row.viewCount || row.count || 0}
            </span>
          </div>
        ),
        sortable: false,
        width: '120px',
      },
      // {
      //   name: t('History'),
      //   cell: (row: any) => (
      //     <div className='my-2'>
      //       <button
      //         className='btn btn-light-primary btn-sm d-flex align-items-center gap-1 px-3 py-2 rounded-pill'
      //         onClick={() => openUserHistory(row)}
      //       >
      //         <KTIcon iconName='time' className='fs-4' iconType='solid' />
      //         <span className='fs-7'>{t('History')}</span>
      //       </button>
      //     </div>
      //   ),
      //   sortable: false,
      //   width: '130px',
      // },
    ],
    [t, selectedSponser, imageBaseUrl]
  )

  // ── Effects ──
  useEffect(() => {
    fetchImpressionReport()
  }, [limit, offset, sortField, sortOrder, searchTerm])

  useEffect(() => {
    document.title = 'Insayr | Search Ad Impressions'
  }, [])

  return (
    <>
      <ToastContainer />
      {isLoading && <Loader />}

      {/* Info Banner */}
      <div className='imp-info-banner'>
        <KTIcon iconName='chart-simple' className='fs-1 text-primary flex-shrink-0' />
        <div>
          <div className='fw-bold fs-6 text-primary mb-1'>{t('Search Ad Impression Report')}</div>
          <p className='text-gray-700 fs-7 mb-0'>
            {t('View impression statistics for all search ads. Click "View Users" to see which users viewed each search ad.')}
          </p>
        </div>
      </div>

      {/* Main Table */}
      <DataTable
        columns={columns}
        data={reportList}
        pagination
        paginationServer
        onChangePage={handlePageChange}
        paginationTotalRows={totalRecords}
        paginationPerPage={limit}
        onChangeRowsPerPage={handleRowsPerPageChange}
        paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
        sortServer
        onSort={handleSort}
        subHeader
        subHeaderComponent={<CustomHeader search={inputValue} isfoc={focus} customSearch={sponserSearch} />}
        paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
        progressPending={isLoading}
        progressComponent={
          <div className='imp-table-loader'>
            <span className='spinner-border spinner-border-sm text-primary me-2' />
            <span className='text-muted fs-7'>{t('Loading data...')}</span>
          </div>
        }
        noDataComponent={
          <div className='imp-empty-state'>
            <KTIcon iconName='chart-simple' className='fs-3x text-muted mb-3' />
            <div className='fw-bold fs-5 text-gray-600 mb-1'>{t('No Impression Data')}</div>
            <p className='text-muted fs-7'>{t('No impression reports found.')}</p>
          </div>
        }
      />

      {/* ── View Users Modal ── */}
      <Modal show={showUsersModal} onHide={closeUsersModal} centered size='lg' className='imp-modal-fixed'>
        <Modal.Header closeButton className='imp-modal-header'>
          <Modal.Title className='fw-bold fs-4 d-flex align-items-center gap-2'>
            <KTIcon iconName='people' className='fs-2 text-info' iconType='solid' />
            <span>{t('Search Ad Viewers')}</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className='imp-modal-body'>
          {/* Sponser summary */}
          {selectedSponser && (
            <div className='d-flex flex-wrap align-items-center gap-4 p-4 mb-4 rounded-3' style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
              <div className='flex-grow-1'>
                <div className='fw-bold fs-5 text-dark'>
                  {selectedSponser.en_parentName ? selectedSponser.en_parentName : (selectedSponser.en_name || '-')}
                </div>
                {selectedSponser.en_parentName && selectedSponser.en_name && (
                  <div className='text-muted fs-7'>{selectedSponser.en_name}</div>
                )}
                <div className='text-muted fs-8 mt-1'>
                  {selectedSponser.fullName || '-'} · {selectedSponser.en_categoryName || '-'}
                </div>
              </div>
              <div className='d-flex gap-3'>
                <div className='text-center px-3'>
                  <div className='fw-bolder fs-4 text-primary'>{(Number(selectedSponser.totalImpressions) || 0).toLocaleString()}</div>
                  <div className='text-muted fs-8'>{t('Impressions')}</div>
                </div>
                <div className='text-center px-3' style={{ borderLeft: '1px solid #E2E8F0' }}>
                  <div className='fw-bolder fs-4 text-info'>{(Number(selectedSponser.totalViews) || 0).toLocaleString()}</div>
                  <div className='text-muted fs-8'>{t('Clicks')}</div>
                </div>
                <div className='text-center px-3' style={{ borderLeft: '1px solid #E2E8F0' }}>
                  <div className='fw-bolder fs-4 text-success'>{selectedSponser.ctr ? `${selectedSponser.ctr}%` : '-'}</div>
                  <div className='text-muted fs-8'>{t('CTR')}</div>
                </div>
              </div>
            </div>
          )}

          {/* ── Users List View ── */}
          <>
            {usersLoading ? (
                <div className='imp-modal-loader'>
                  <div className='imp-skeleton-list'>
                    {[1, 2, 3, 4].map((i) => (
                      <div className='imp-skeleton-row' key={i}>
                        <div className='imp-skeleton imp-skeleton--circle' />
                        <div className='imp-skeleton-text'>
                          <div className='imp-skeleton imp-skeleton--line-lg' />
                          <div className='imp-skeleton imp-skeleton--line-sm' />
                        </div>
                        <div className='imp-skeleton imp-skeleton--badge' />
                      </div>
                    ))}
                  </div>
                </div>
              ) : usersList.length === 0 ? (
                <div className='imp-empty-state'>
                  <KTIcon iconName='people' className='fs-3x text-muted mb-3' />
                  <div className='fw-bold fs-5 text-gray-600 mb-1'>{t('No Viewers')}</div>
                  <p className='text-muted fs-7'>{t('No users have viewed this search ad yet.')}</p>
                </div>
              ) : (
                <DataTable
                  columns={usersColumns}
                  data={usersList}
                  pagination
                  paginationServer
                  onChangePage={handleUsersPageChange}
                  paginationTotalRows={usersTotal}
                  paginationPerPage={usersLimit}
                  onChangeRowsPerPage={handleUsersRowsPerPageChange}
                  paginationRowsPerPageOptions={[5, 10, 15, 20]}
                  paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
                  progressPending={usersLoading}
                  progressComponent={
                    <div className='imp-table-loader'>
                      <span className='spinner-border spinner-border-sm text-primary me-2' />
                      <span className='text-muted fs-7'>{t('Loading users...')}</span>
                    </div>
                  }
                />
              )}
            </>
        </Modal.Body>

        <Modal.Footer className='imp-modal-footer'>
          <button className='btn btn-light btn-sm px-5' onClick={closeUsersModal}>
            {t('Close')}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export { SearchAdImpression }
